import { useMemo } from 'react';
import type { FC } from 'react';
import { useScript } from 'usehooks-ts';
import { useSLTConfig } from '../../slt/config/SLTConfigProvider';
import { useAppleRequest } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isHydrated } from '../../utils/utils';
import { APPLE_PAY_VERSION, ApplePay } from './ApplePay';
import type { Basket } from '../../bff-api/baskets/model';
import type { Product } from '../../bff-api/products/model';
import type { RequestParams, StepType } from './ApplePay';

export const shouldShowApplePay = (): boolean => {
    try {
        return (
            typeof ApplePaySession !== 'undefined' &&
            ApplePaySession.canMakePayments() &&
            ApplePaySession.supportsVersion(APPLE_PAY_VERSION) &&
            isHydrated()
        );
    } catch (err) {
        return false;
    }
};

const APPLEPAY_JS_SCRIPT = 'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js';

interface ApplePayButtonProps {
    step: StepType;
    basket?: Basket;
    product?: Product;
    params?: RequestParams;
    isDisabled?: boolean;
}

const ApplePayButton: FC<ApplePayButtonProps> = ({
    basket,
    product,
    step,
    params = {},
    isDisabled = false,
}) => {
    const { customPreferences } = useSLTConfig();

    const enabledOnPdp = customPreferences[CustomPreference.APPLE_PAY_ENABLED_ON_NEW_PDP];
    const enabledOnCheckout = customPreferences[CustomPreference.APPLE_PAY_ENABLED_ON_CHECKOUT];
    const enabledOnGdp = customPreferences[CustomPreference.APPLE_PAY_ENABLED_ON_NEW_GDP];

    const enabledByStep: Partial<Record<StepType, boolean>> = {
        newpdp: Boolean(enabledOnPdp),
        newgdp: Boolean(enabledOnGdp),
    };
    const enabled = step in enabledByStep ? enabledByStep[step] : Boolean(enabledOnCheckout);

    const applePayScriptStatus = useScript(APPLEPAY_JS_SCRIPT, {
        removeOnUnmount: false,
        shouldPreventLoad: !enabled,
    });

    const showApplePay = useMemo(
        () => applePayScriptStatus === 'ready' && shouldShowApplePay(),
        [applePayScriptStatus],
    );

    const applePayRequest = useAppleRequest(
        showApplePay,
        params as Record<string, string | number | boolean>,
    );

    if (!showApplePay || !applePayRequest?.data) {
        return null;
    }

    return (
        <ApplePay
            basket={basket}
            product={product}
            applePayRequest={applePayRequest.data.request}
            step={step}
            params={params}
            isDisabled={isDisabled}
        />
    );
};

ApplePayButton.defaultProps = {
    basket: undefined,
    product: undefined,
    params: undefined,
    isDisabled: false,
};

export { ApplePayButton };
